// @ts-check
// Resumo antes da visita e registro da visita (/visits/:enrollmentOpportunityId, contrato 08 de visitas, seção 7).
// Feito para o tablet ou o celular durante a visita: blocos grandes, telefones e e-mails clicáveis, ações no topo.
// Tudo o que se registra aqui vai para o card (nota, comparecimento, data, quem conduz e os dados completados).
import { html } from 'htm/preact';
import { useEffect, useMemo, useState } from 'preact/hooks';
import { Link, navigate, searchParamsState } from '../../app/router.js';
import { addVisitNote, fetchVisitSummary, recordVisitAttendance } from '../../data/visits.js';
import { currentWorkspaceMemberState, objectsByNameSingular } from '../../data/metadata-store.js';
import { createLiveRecordList, mutationCounterState } from '../../data/record-store.js';
import { findManyRecords, updateOneRecord } from '../../data/rest.js';
import { formatDateTime, formatDate } from '../../lib/format.js';
import { isFieldReadOnly, isFieldValueEmpty } from '../../lib/field-metadata.js';
import { canUpdateObjectRecords } from '../../lib/permissions.js';
import { markdownToHtml } from '../../lib/rich-text.js';
import { isSupportMember } from '../../../../shared/support/support-member.js';
import { PageCard, PageHeader } from '../page/page-card.js';
import { InlineCell } from '../record-show/inline-cell.js';
import { Button } from '../../ui/button.js';
import { Tag } from '../../ui/chip.js';
import { Icon } from '../../ui/icon.js';
import { Modal } from '../../ui/modal.js';
import { showErrorSnackbar, showSuccessSnackbar } from '../../ui/snackbar.js';
import { VisitSituationTag, eventTypeColor } from './visits-page.js';
import { t } from '../../i18n/index.js';
import { maskPostcode, readPostcodeDigits } from '../../../../shared/address/postcode-autofill.js';

/** @typedef {import('../../data/visits.js').VisitSummary} VisitSummary */

const EVENT_FIELDS = /** @type {Record<string, string>} */ ({
  VISIT: 'visitScheduledAt', ASSESSMENT: 'assessmentScheduledAt', TRIAL_DAY: 'trialDayScheduledAt',
  SCHOLARSHIP_EXAM: 'scholarshipExamScheduledAt', OPEN_SCHOOL: 'openSchoolScheduledAt',
});
// campos que a visita costuma completar; só aparecem os que o papel lê e pode editar
const CARD_FIELDS = ['visitHost', 'interestLevel', 'campus', 'academicPeriod', 'tags'];
const STUDENT_PERSON_FIELDS = ['birthDate', 'originSchool', 'currentGradeLevel', 'specialEducationalNeeds', 'phones', 'emails'];
const STUDENT_FIELDS = ['course', 'classOffering'];
// o endereço é o da família (CEP só na família, 1.12.1): o do responsável não aparece nem se completa aqui
const GUARDIAN_FIELDS = ['phones', 'emails'];

/** Valor de <input type="datetime-local"> no fuso do navegador. @param {Date} date */
function toLocalInputValue(date) {
  const local = new Date(date.getTime() - date.getTimezoneOffset() * 60000);
  return local.toISOString().slice(0, 16);
}

/** @param {{ title: string, icon: string, children: any, className?: string }} props */
function SummaryBlock({ title, icon, children, className = '' }) {
  return html`<section class=${`visit-block ${className}`}>
    <h3 class="visit-block-title"><${Icon} name=${icon} size=${16} /> ${title}</h3>
    ${children}
  </section>`;
}

/** @param {{ phone?: string | null, email?: string | null }} props */
function ContactLinks({ phone, email }) {
  return html`<div class="visit-contact-links">
    ${phone ? html`<a href=${`tel:${phone.replace(/\s/g, '')}`}><${Icon} name="IconPhone" size=${14} /> ${phone}</a>` : null}
    ${email ? html`<a href=${`mailto:${email}`}><${Icon} name="IconMail" size=${14} /> ${email}</a>` : null}
  </div>`;
}

/** CEP com a máscara quando completo; incompleto, como foi digitado. @param {unknown} postcode */
const formatPostcode = (postcode) => (readPostcodeDigits(postcode) ? maskPostcode(String(postcode)) : String(postcode ?? '').trim());

/** @param {any} address */
const formatAddress = (address) => (address
  ? [address.addressStreet1, address.addressStreet2, address.addressCity, address.addressState, formatPostcode(address.addressPostcode)].filter(Boolean).join(', ')
  : '');

/**
 * O mesmo aluno em duas linhas do card (escola e curso de idiomas) aparece uma vez, com as séries juntas.
 * @param {Array<Record<string, any>>} students
 */
function groupStudents(students) {
  /** @type {Map<string, Record<string, any>>} */
  const byPerson = new Map();
  for (const student of students) {
    const key = student.personId ?? student.opportunityStudentId;
    const current = byPerson.get(key);
    if (current) {
      if (student.grade && !current.grades.includes(student.grade)) current.grades.push(student.grade);
      continue;
    }
    byPerson.set(key, { ...student, grades: student.grade ? [student.grade] : [] });
  }
  return [...byPerson.values()];
}

/**
 * Campos editáveis de vários registros de um objeto, com os vazios marcados.
 * @param {{ objectName: string, recordIds: string[], fieldNames: string[], titleOf: (record: any) => string }} props
 */
function CompleteFields({ objectName, recordIds, fieldNames, titleOf }) {
  const objectMetadata = objectsByNameSingular.value.get(objectName);
  const list = useMemo(() => createLiveRecordList(objectName), [objectName]);
  useEffect(() => () => list.dispose(), [list]);
  useEffect(() => {
    if (!objectMetadata || recordIds.length === 0) return;
    // depth 1: relações (quem conduz, unidade, escola de origem) chegam com o nome
    findManyRecords(objectMetadata, { filter: { field: 'id', operator: 'in', value: recordIds }, limit: Math.min(200, recordIds.length), depth: 1 })
      .then(({ records }) => {
        list.records.value = recordIds.map((id) => records.find((record) => record.id === id)).filter(Boolean);
      })
      .catch(() => {});
  }, [objectMetadata, recordIds.join(',')]);
  if (!objectMetadata || !canUpdateObjectRecords(objectMetadata)) return null;
  const fields = fieldNames
    .map((name) => objectMetadata.fields.find((field) => field.name === name))
    .filter((field) => field && !isFieldReadOnly(field));
  if (fields.length === 0) return null;
  return html`${list.records.value.map((record) => html`<div key=${record.id} class="visit-complete-record">
    <div class="visit-complete-record-title">${titleOf(record)}</div>
    ${fields.map((field) => html`<div key=${field.id} class="visit-complete-field" data-empty=${isFieldValueEmpty(field, record[field.name]) ? 'true' : undefined}>
      <${InlineCell} objectMetadata=${objectMetadata} field=${field} record=${record} />
    </div>`)}
  </div>`)}`;
}

/**
 * @param {{ enrollmentOpportunityId: string }} props
 */
export function VisitSummaryPage({ enrollmentOpportunityId }) {
  const eventType = searchParamsState.value.get('type') ?? 'VISIT';
  const [summary, setSummary] = useState(/** @type {VisitSummary | null} */ (null));
  const [noteText, setNoteText] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [dialog, setDialog] = useState(/** @type {null | 'attended' | 'reschedule'} */ (null));
  const [dateValue, setDateValue] = useState('');
  const reload = () => fetchVisitSummary(enrollmentOpportunityId).then(setSummary).catch(showErrorSnackbar);
  const cardObject = objectsByNameSingular.value.get('enrollmentOpportunity');
  const canEditCard = Boolean(cardObject && canUpdateObjectRecords(cardObject));
  // "Eu conduzo" não vale na sessão do suporte: ele não é da equipe da escola (contrato 08 de cobrança, seção 7.3)
  const me = isSupportMember(currentWorkspaceMemberState.value) ? null : currentWorkspaceMemberState.value;

  useEffect(() => {
    reload();
  }, [enrollmentOpportunityId, mutationCounterState.value]);
  useEffect(() => {
    if (summary) document.title = `${summary.family?.name ?? summary.card.name} · ${t('visits.title')}`;
  }, [summary]);

  if (!summary) {
    return html`<${PageCard} header=${html`<${PageHeader} icon="IconDoorEnter" title=${t('visits.title')} />`}><div class="visits-loading">${t('visits.loading')}</div><//>`;
  }
  const card = summary.card;
  const fieldName = EVENT_FIELDS[eventType] ?? 'visitScheduledAt';
  const scheduled = eventType === 'VISIT' ? card.visitScheduledAt : card.scheduledDates.find((/** @type {any} */ date) => date.eventType === eventType)?.startsAt;
  const isVisit = eventType === 'VISIT';
  const studentPersonIds = [...new Set(summary.students.map((student) => student.personId).filter(Boolean))];
  const guardianIds = summary.guardians.map((guardian) => guardian.personId);

  const run = async (/** @type {() => Promise<unknown>} */ action, /** @type {string} */ successKey) => {
    setIsSaving(true);
    try {
      const result = /** @type {any} */ (await action());
      const missing = Array.isArray(result?.stageRequirementsMissing) ? result.stageRequirementsMissing : [];
      // comparecimento gravado, mas o card só vai para Visita realizada com a ficha completa
      if (missing.length > 0) showErrorSnackbar(new Error(t('visits.saved.attendedIncomplete', { fields: [...new Set(missing.map((item) => item.label))].join(', ') })));
      else showSuccessSnackbar(t(successKey));
      setDialog(null);
      await reload();
    } catch (error) {
      showErrorSnackbar(error);
    } finally {
      setIsSaving(false);
    }
  };
  const openAttended = () => {
    const at = scheduled && new Date(scheduled) <= new Date() ? new Date(scheduled) : new Date();
    setDateValue(toLocalInputValue(at));
    setDialog('attended');
  };
  const openReschedule = () => {
    setDateValue(toLocalInputValue(scheduled ? new Date(scheduled) : new Date()));
    setDialog('reschedule');
  };
  const takeVisit = () => run(async () => {
    if (!cardObject || !me) return;
    await updateOneRecord(cardObject, card.id, { visitHostId: me.id });
  }, 'visits.saved.host');

  const actions = html`<div class="visit-actions">
    ${isVisit && canEditCard
      ? html`
          <${Button} title=${t('visits.actions.attended')} icon="IconCheck" accent="blue" disabled=${card.visitAttendance === 'ATTENDED'} onClick=${openAttended} />
          <${Button} title=${t('visits.actions.noShow')} icon="IconUserX" variant="secondary" accent="danger" disabled=${card.visitAttendance === 'NO_SHOW'}
            onClick=${() => run(() => recordVisitAttendance(card.id, { attendance: 'NO_SHOW' }), 'visits.saved.noShow')} />`
      : null}
    ${canEditCard ? html`<${Button} title=${t('visits.actions.reschedule')} icon="IconCalendarTime" variant="secondary" onClick=${openReschedule} />` : null}
    ${canEditCard && me && card.visitHost?.id !== me.id
      ? html`<${Button} title=${t('visits.actions.takeVisit')} icon="IconDoorEnter" variant="secondary" onClick=${takeVisit} />`
      : null}
  </div>`;

  return html`<${PageCard}
    className="visit-summary-card"
    header=${html`<${PageHeader}
      icon="IconDoorEnter"
      breadcrumb=${html`<${Link} href="/visits" className="visit-breadcrumb">${t('visits.title')}</${Link}>`}
      title=${summary.family?.name ?? card.name}
      actions=${html`<${Button} title=${t('visits.openCard')} icon="IconExternalLink" variant="secondary" size="small"
        onClick=${() => navigate(`/object/enrollmentOpportunity/${card.id}`)} />`}
    />`}
  >
    <div class="visit-summary" data-testid="visit-summary">
      <div class="visit-hero">
        <div class="visit-hero-main">
          <div class="visit-hero-tags">
            <${Tag} color=${eventTypeColor(eventType)} text=${t(`calendar.eventTypes.${eventType}`)} />
            ${isVisit && card.visitSituation ? html`<${VisitSituationTag} situation=${card.visitSituation} needsCheckIn=${card.needsCheckIn} />` : null}
            ${card.stage ? html`<${Tag} color=${card.stage.color ?? 'gray'} text=${card.stage.label} />` : null}
          </div>
          <div class="visit-hero-when">${scheduled ? formatDateTime(scheduled) : t('visits.noDate')}</div>
          <div class="visit-hero-meta">
            ${card.campus ? html`<span><${Icon} name="IconBuildingCommunity" size=${14} /> ${card.campus.name}</span>` : null}
            <span><${Icon} name="IconDoorEnter" size=${14} /> ${card.visitHost?.name ?? t('visits.noHost')}</span>
            ${card.owner ? html`<span><${Icon} name="IconUserCircle" size=${14} /> ${t('visits.consultant', { name: card.owner.name })}</span>` : null}
            ${card.academicPeriod ? html`<span><${Icon} name="IconCalendarStats" size=${14} /> ${card.academicPeriod.name}</span>` : null}
          </div>
        </div>
        ${actions}
      </div>

      <div class="visit-columns">
        <div class="visit-column">
          <${SummaryBlock} title=${t('visits.blocks.guardians')} icon="IconUsers">
            ${summary.guardians.length === 0 ? html`<p class="visit-muted">${t('visits.noGuardians')}</p>` : null}
            ${summary.guardians.map((guardian) => html`<div key=${guardian.personId} class="visit-person">
              <div class="visit-person-name">
                ${guardian.name}
                ${guardian.kinship ? html`<span class="visit-muted"> · ${guardian.kinship.label}</span>` : null}
                ${guardian.isPointOfContact ? html` <${Tag} color="blue" text=${t('visits.pointOfContact')} />` : null}
              </div>
              <${ContactLinks} phone=${guardian.phone} email=${guardian.email} />
            </div>`)}
            ${summary.family?.address && formatAddress(summary.family.address) ? html`<div class="visit-muted">${t('visits.familyAddress')}: ${formatAddress(summary.family.address)}</div>` : null}
          <//>

          <${SummaryBlock} title=${t('visits.blocks.students')} icon="IconSchool">
            ${summary.students.length === 0 ? html`<p class="visit-muted">${t('visits.noStudents')}</p>` : null}
            ${groupStudents(summary.students).map((student) => html`<div key=${student.opportunityStudentId} class="visit-person">
              <div class="visit-person-name">
                ${student.name}
                ${student.age !== null ? html`<span class="visit-muted"> · ${t('visits.age', { count: student.age })}</span>` : null}
                ${student.stage && student.stage.value !== card.stage?.value ? html` <${Tag} color=${student.stage.color ?? 'gray'} text=${student.stage.label} />` : null}
              </div>
              <dl class="visit-facts">
                <dt>${t('visits.intendedGrade')}</dt><dd>${student.grades.join(', ') || t('visits.notInformed')}</dd>
                <dt>${t('visits.originSchool')}</dt><dd>${student.originSchool?.name ?? t('visits.notInformed')}</dd>
                ${student.currentGradeLevel ? html`<dt>${t('visits.currentGrade')}</dt><dd>${student.currentGradeLevel.label}</dd>` : null}
                ${student.birthDate ? html`<dt>${t('visits.birthDate')}</dt><dd>${formatDate(student.birthDate)}</dd>` : null}
              </dl>
              ${student.specialEducationalNeeds
                ? html`<div class="visit-alert"><${Icon} name="IconAlertTriangle" size=${14} /> ${t('visits.specialNeeds')}: ${student.specialEducationalNeeds}</div>`
                : null}
            </div>`)}
          <//>

          ${summary.siblingsEnrolled.length > 0
            ? html`<${SummaryBlock} title=${t('visits.blocks.siblings')} icon="IconUsersGroup">
                <ul class="visit-plain-list">
                  ${summary.siblingsEnrolled.map((sibling) => html`<li key=${sibling.enrollmentId}>${sibling.name}${sibling.classOffering ? ` · ${sibling.classOffering.name}` : ''}</li>`)}
                </ul>
              <//>`
            : null}

          <${SummaryBlock} title=${t('visits.blocks.origin')} icon="IconSpeakerphone">
            <dl class="visit-facts">
              <dt>${t('visits.leadSource')}</dt><dd>${summary.origin.leadSource?.label ?? t('visits.notInformed')}</dd>
              ${summary.origin.campaign ? html`<dt>${t('visits.campaign')}</dt><dd>${summary.origin.campaign.name}</dd>` : null}
              ${summary.origin.utmCampaign ? html`<dt>UTM</dt><dd>${summary.origin.utmCampaign}</dd>` : null}
              ${summary.origin.createdAt ? html`<dt>${t('visits.leadSince')}</dt><dd>${formatDate(summary.origin.createdAt)}</dd>` : null}
            </dl>
            ${summary.interests.tags.length > 0 || summary.interests.courses.length > 0
              ? html`<div class="visit-interests">
                  ${summary.interests.tags.map((tag) => html`<${Tag} key=${tag.value} color=${tag.color ?? 'gray'} text=${tag.label} />`)}
                  ${summary.interests.courses.map((course) => html`<${Tag} key=${course} color="gray" text=${course} />`)}
                </div>`
              : null}
            ${card.scheduledDates.filter((/** @type {any} */ date) => date.eventType !== eventType).length > 0
              ? html`<ul class="visit-plain-list">
                  ${card.scheduledDates.filter((/** @type {any} */ date) => date.eventType !== eventType).map((/** @type {any} */ date) => html`<li key=${date.eventType}>
                    ${t(`calendar.eventTypes.${date.eventType}`)}: ${formatDateTime(date.startsAt)}
                  </li>`)}
                </ul>`
              : null}
          <//>
        </div>

        <div class="visit-column">
          <${SummaryBlock} title=${t('visits.blocks.notes')} icon="IconNotes">
            <form class="visit-note-form" onSubmit=${(event) => {
              event.preventDefault();
              if (!noteText.trim()) return;
              run(async () => {
                await addVisitNote(card.id, { body: noteText });
                setNoteText('');
              }, 'visits.saved.note');
            }}>
              <textarea
                class="visit-note-input"
                rows="4"
                placeholder=${t('visits.notePlaceholder')}
                aria-label=${t('visits.blocks.notes')}
                value=${noteText}
                onInput=${(event) => setNoteText(/** @type {HTMLTextAreaElement} */ (event.currentTarget).value)}
              ></textarea>
              <${Button} title=${t('visits.saveNote')} icon="IconDeviceFloppy" type="submit" disabled=${isSaving || !noteText.trim()} />
            </form>
            ${summary.recentNotes === null ? null : summary.recentNotes.length === 0
              ? html`<p class="visit-muted">${t('visits.noNotes')}</p>`
              : summary.recentNotes.map((note) => html`<article key=${note.id} class="visit-note">
                  <header class="visit-note-header">
                    <strong>${note.title || t('visits.untitledNote')}</strong>
                    <span class="visit-muted">${[note.authorName, formatDateTime(note.createdAt)].filter(Boolean).join(' · ')}</span>
                  </header>
                  <div class="visit-note-body rich-text-content" dangerouslySetInnerHTML=${{ __html: markdownToHtml(note.markdown ?? '') }}></div>
                </article>`)}
          <//>

          ${summary.recentMessages
            ? html`<${SummaryBlock} title=${t('visits.blocks.messages')} icon="IconMessages">
                ${summary.recentMessages.length === 0 ? html`<p class="visit-muted">${t('visits.noMessages')}</p>` : null}
                ${summary.recentMessages.map((message) => html`<div key=${message.id} class="visit-message" data-direction=${message.direction}>
                  <div class="visit-muted">${[message.authorName, formatDateTime(message.sentAt)].filter(Boolean).join(' · ')}</div>
                  <div>${message.text ?? (message.hasAttachments ? t('visits.attachment') : '')}</div>
                </div>`)}
              <//>`
            : null}

          <${SummaryBlock} title=${t('visits.blocks.complete')} icon="IconPencil" className="visit-complete">
            <p class="visit-muted">${t('visits.completeHint')}</p>
            <${CompleteFields} objectName="enrollmentOpportunity" recordIds=${[card.id]} fieldNames=${CARD_FIELDS} titleOf=${() => t('visits.cardFields')} />
            <${CompleteFields} objectName="opportunityStudent" recordIds=${summary.students.map((student) => student.opportunityStudentId)} fieldNames=${STUDENT_FIELDS}
              titleOf=${(record) => summary.students.find((student) => student.opportunityStudentId === record.id)?.name ?? ''} />
            <${CompleteFields} objectName="person" recordIds=${studentPersonIds} fieldNames=${STUDENT_PERSON_FIELDS}
              titleOf=${(record) => summary.students.find((student) => student.personId === record.id)?.name ?? ''} />
            ${summary.family
              ? html`<${CompleteFields} objectName="household" recordIds=${[summary.family.id]} fieldNames=${['householdAddress']} titleOf=${() => summary.family?.name ?? ''} />`
              : null}
            <${CompleteFields} objectName="person" recordIds=${guardianIds} fieldNames=${GUARDIAN_FIELDS}
              titleOf=${(record) => summary.guardians.find((guardian) => guardian.personId === record.id)?.name ?? ''} />
          <//>
        </div>
      </div>
    </div>

    ${dialog === 'attended'
      ? html`<${Modal}
          title=${t('visits.attendedDialog.title')}
          subtitle=${t('visits.attendedDialog.subtitle')}
          confirmLabel=${t('visits.actions.attended')}
          isLoading=${isSaving}
          isConfirmDisabled=${!dateValue}
          onClose=${() => setDialog(null)}
          onConfirm=${() => run(() => recordVisitAttendance(card.id, { attendance: 'ATTENDED', occurredAt: new Date(dateValue).toISOString() }), 'visits.saved.attended')}
        >
          <label class="visit-dialog-field">
            <span>${t('visits.attendedDialog.when')}</span>
            <input type="datetime-local" class="text-input" value=${dateValue} onInput=${(event) => setDateValue(/** @type {HTMLInputElement} */ (event.currentTarget).value)} />
          </label>
        <//>`
      : null}
    ${dialog === 'reschedule'
      ? html`<${Modal}
          title=${t('visits.rescheduleDialog.title', { type: t(`calendar.eventTypes.${eventType}`) })}
          subtitle=${scheduled ? t('visits.rescheduleDialog.current', { when: `${formatDateTime(scheduled)}` }) : undefined}
          confirmLabel=${t('visits.actions.reschedule')}
          isLoading=${isSaving}
          isConfirmDisabled=${!dateValue}
          onClose=${() => setDialog(null)}
          onConfirm=${() => run(async () => {
            if (!cardObject) return;
            await updateOneRecord(cardObject, card.id, { [fieldName]: new Date(dateValue).toISOString() });
          }, 'visits.saved.rescheduled')}
        >
          <label class="visit-dialog-field">
            <span>${t('visits.rescheduleDialog.when')}</span>
            <input type="datetime-local" class="text-input" value=${dateValue} onInput=${(event) => setDateValue(/** @type {HTMLInputElement} */ (event.currentTarget).value)} />
          </label>
        <//>`
      : null}
  <//>`;
}
